import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { DataSourceOrdenacaoInsensivel } from '@shared/helpers/ordenacao-tabela.helper';
import { formatarCadastrosVinculados } from '@shared/helpers/vinculo-tipo-tributario.helper';
import { HeaderComponent } from '@shared/components/header/header.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '@shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { UtilService } from '@shared/services/util.service';
import { TipoTributarioService } from '@shared/services/apis/motor-fiscal/tipo-tributario.service';
import { PessoaService } from '@shared/services/apis/pessoa/pessoa.service';
import { TipoTributarioStruct } from '@shared/services/structs/motor-fiscal/tipo-tributario.struct';
import {
  ResolverVinculoModalComponent,
  ResolverVinculoModalResult,
} from './resolver-vinculo-modal/resolver-vinculo-modal.component';

@Component({
  selector: 'app-config-tipos-tributarios',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, RouterModule,
    MatButtonModule, MatCheckboxModule, MatDialogModule, MatFormFieldModule, MatIconModule,
    MatInputModule, MatMenuModule, MatProgressSpinnerModule, MatSortModule, MatTableModule,
    MatTooltipModule,
    HeaderComponent, EmptyListComponent, LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-tipos-tributarios.component.html',
  styleUrl: './config-tipos-tributarios.component.css',
})
export class ConfigTiposTributariosComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;

  public _dataSource = new DataSourceOrdenacaoInsensivel<TipoTributarioStruct>();
  public _isLoading = false;
  public _totalRegistros = 0;
  private _paginaAtual = 1;
  private readonly tamanhoPagina = 20;

  public displayedColumns = ['select', 'codigo', 'descricao', 'status', 'actions'];
  public selection = new SelectionModel<TipoTributarioStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private tipoTributarioService: TipoTributarioService,
    private pessoaService: PessoaService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({ pesquisa: [''] });

    this.loadFilters();
    this.carregar(true);
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/tipos-tributarios/cadastrar',
      '/configuracoes/cadastros/tipos-tributarios/cadastrar/',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  // ── Listagem ──────────────────────────────────────────────────────────────

  public carregar(reset: boolean = false): void {
    if (reset) {
      this._paginaAtual = 1;
      this.selection.clear();
    }
    this._isLoading = true;

    this.tipoTributarioService.getAll({
      pesquisa: this.model.get('pesquisa')?.value || undefined,
      paginaAtual: this._paginaAtual,
      tamanhoPagina: this.tamanhoPagina,
    }).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar os tipos tributários', AlertType.error);
          return;
        }
        this._totalRegistros = response.totalRegistros;
        this._dataSource.data = reset ? response.itens : [...this._dataSource.data, ...response.itens];
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message || 'Erro ao carregar os tipos tributários', AlertType.error);
      },
    });
  }

  public clickPesquisar(): void {
    this._dataSource.data = [];
    this.carregar(true);
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregar();
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/cadastros']);
  }

  public novoTipoTributario(): void {
    this.router.navigate(['/configuracoes/cadastros/tipos-tributarios/cadastrar']);
  }

  public editarTipoTributario(id: number): void {
    this.router.navigate(['/configuracoes/cadastros/tipos-tributarios/cadastrar', id]);
  }

  // ── Seleção ───────────────────────────────────────────────────────────────

  public isAllSelected(): boolean {
    return this._dataSource.data.length > 0 && this.selection.selected.length === this._dataSource.data.length;
  }

  public toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this._dataSource.data);
    }
  }

  public getExcluirTooltip(): string {
    return this.selection.hasValue() ? '' : 'Selecione ao menos um registro.';
  }

  // ── Exclusão ──────────────────────────────────────────────────────────────

  public excluir(item: TipoTributarioStruct): void {
    this.iniciarExclusao([item]);
  }

  public excluirSelecionados(): void {
    if (!this.selection.hasValue()) {
      this.alertService.show('Atenção', 'Selecione ao menos um registro.', AlertType.warning);
      return;
    }

    this.iniciarExclusao(this.selection.selected);
  }

  /**
   * Antes de excluir, consulta na API Pessoa quantos cadastros usam os tipos selecionados
   * (cross-database). Havendo vínculo, a resolução é solicitada uma única vez (RN09/RN11).
   *
   * Uma falha aqui NÃO pode virar "0 vínculos": sem FK entre as bases, isso excluiria o tipo
   * tributário e deixaria cadastros de pessoa/vendedor órfãos, exatamente o que a RN09 evita.
   */
  private iniciarExclusao(itens: TipoTributarioStruct[]): void {
    const ids = itens.map(i => i.idTipoTributario);

    this.pessoaService.contarUsoTipoTributario(ids).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Não foi possível verificar vínculos com cadastros de pessoa.', AlertType.error);
          return;
        }
        if (response.total > 0) {
          this.abrirResolucaoVinculo(itens, response.totalPessoas, response.totalVendedores);
          return;
        }
        this.confirmarExclusaoSimples(itens);
      },
      error: error => this.alertService.show('Erro inesperado', error.message || 'Não foi possível verificar vínculos com cadastros de pessoa.', AlertType.error),
    });
  }

  private confirmarExclusaoSimples(itens: TipoTributarioStruct[]): void {
    const isBulk = itens.length > 1;
    const descricao = isBulk
      ? `${itens.length} tipo(s) tributário(s) selecionado(s) serão removidos permanentemente. Esta ação não pode ser desfeita.`
      : `O tipo tributário "${itens[0].codigo} — ${itens[0].descricao}" será removido permanentemente. Esta ação não pode ser desfeita.`;

    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: isBulk ? 'Excluir registros?' : 'Excluir registro?',
        description: descricao,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    ref.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.executarExclusao(itens, isBulk
        ? `${itens.length} registro(s) excluído(s).`
        : 'Registro excluído.');
    });
  }

  /**
   * Os candidatos à reatribuição são os tipos ativos fora da seleção (RN11). A lista vem
   * da API porque a listagem é paginada e pode não conter todos os tipos cadastrados.
   *
   * Uma falha aqui NÃO pode virar "lista vazia": o modal interpretaria isso como a RN10
   * ("não há outro tipo tributário ativo") e ofereceria apenas desvincular, zerando o tipo
   * tributário dos cadastros mesmo havendo destinos válidos.
   */
  private abrirResolucaoVinculo(itens: TipoTributarioStruct[], totalPessoas: number, totalVendedores: number): void {
    const idsExcluidos = new Set(itens.map(i => i.idTipoTributario));

    this.tipoTributarioService.getAll({ isAtivo: true, paginaAtual: 1, tamanhoPagina: 500 }).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar os tipos tributários para reatribuição.', AlertType.error);
          return;
        }
        this.abrirModalVinculo(itens, totalPessoas, totalVendedores,
          response.itens.filter(t => !idsExcluidos.has(t.idTipoTributario)));
      },
      error: error => this.alertService.show('Erro inesperado', error.message || 'Não foi possível carregar os tipos tributários para reatribuição.', AlertType.error),
    });
  }

  private abrirModalVinculo(
    itens: TipoTributarioStruct[],
    totalPessoas: number,
    totalVendedores: number,
    candidatos: TipoTributarioStruct[]
  ): void {
    const isBulk = itens.length > 1;
    const vinculos = formatarCadastrosVinculados(totalPessoas, totalVendedores);

    const ref = this.dialog.open(ResolverVinculoModalComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        isBulk,
        descricaoAlvo: isBulk ? '' : `${itens[0].codigo} — ${itens[0].descricao}`,
        totalPessoas,
        totalVendedores,
        candidatos,
      },
    });

    ref.afterClosed().subscribe((result: ResolverVinculoModalResult | undefined) => {
      if (!result?.confirm) return;

      const destino = result.idTipoTributarioDestino;
      this.pessoaService.reatribuirTipoTributario({
        idsTipoTributarioOrigem: itens.map(i => i.idTipoTributario),
        idTipoTributarioDestino: destino,
      }).subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show('Atenção', response.errorDescription || 'Erro ao resolver os vínculos', AlertType.warning);
            return;
          }
          const codigoDestino = candidatos.find(c => c.idTipoTributario === destino)?.codigo ?? '';
          this.executarExclusao(
            itens,
            destino != null
              ? `${vinculos} reatribuído(s) ao tipo "${codigoDestino}".`
              : `${vinculos} ficaram sem tipo tributário.`,
            destino != null
              ? `reatribuídos ao tipo "${codigoDestino}"`
              : 'deixados sem tipo tributário'
          );
        },
        error: error => this.alertService.show('Erro inesperado', error.message || 'Erro ao resolver os vínculos', AlertType.error),
      });
    });
  }

  /**
   * @param vinculosResolvidos Preenchido apenas quando a exclusão vem da resolução de vínculo,
   * descrevendo o que já foi feito com os cadastros. A reatribuição é commitada na API Pessoa
   * ANTES da exclusão e não é desfeita: se a exclusão falhar, o usuário precisa saber que os
   * cadastros já mudaram, senão o "Erro ao excluir" sugere que nada aconteceu.
   */
  private executarExclusao(itens: TipoTributarioStruct[], mensagemSucesso: string, vinculosResolvidos?: string): void {
    const ids = itens.map(i => i.idTipoTributario);
    const request$ = ids.length > 1
      ? this.tipoTributarioService.bulkDelete({ ids })
      : this.tipoTributarioService.excluir(ids[0]);

    const mensagemErro = (detalhe: string) => vinculosResolvidos
      ? `Os cadastros vinculados já foram ${vinculosResolvidos}, mas a exclusão do tipo tributário falhou: ${detalhe} Tente excluir novamente.`
      : detalhe;

    request$.subscribe({
      next: response => {
        // A exclusão em massa pode ser parcial: recarrega em qualquer caso para não
        // deixar a tela mostrando tipos que já foram excluídos.
        this.clickPesquisar();
        if (response.isError) {
          this.alertService.show('Atenção', mensagemErro(response.errorDescription || 'Erro ao excluir.'), AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', mensagemSucesso, AlertType.success);
      },
      error: error => this.alertService.show('Erro inesperado', mensagemErro(error.message || 'Erro ao excluir.'), AlertType.error),
    });
  }
}
